// Supermetrics API integration utility
import { createClient } from '@supabase/supabase-js';
import { projectId, publicAnonKey } from './supabase/info';
import { EDGE_FUNCTIONS_BASE } from './edge';

// Supermetrics API constants
const API_BASE = "https://api.supermetrics.com/enterprise/v2/query/data/json";
const DS_ID = "FA";
// NOTE: These are now configured per-organization in the organization settings
// DS_ACCOUNTS and DS_USER should be passed from the OrganizationContext
const COMPARE_TYPE = "prev_range";
const COMPARE_SHOW = "value";
const DEFAULT_MAX_ROWS = 1000;

// Supabase URL for edge functions
const SUPABASE_FUNCTIONS_URL = EDGE_FUNCTIONS_BASE;

// Fields that match your Supabase table schema
const FIELDS = [
  "ad_name",
  "creative_title",
  "creative_image_url",
  "adcampaign_name",
  "ad_id",
  "instagram_story_preview_url",
  "instagram_preview_url",
  "facebook_story_preview_url",
  "mobile_feed_preview_url",
  "desktop_feed_preview_url",
  "mobile_interstitial_preview_url",
  "mobile_banner_preview_url",
  "right_column_preview_url",
  "cost_usd",
  "impressions",
  "unique_action_link_click",
  "link_CTR",
  "CPC",
  "CPM",
  "Frequency",
  "reach",
  "quality_score_organic",
  "quality_score_ectr",
  "ROAS"
].join(",");

// Default filter
const DEFAULT_FILTER = "adsetstatus == ACTIVE AND adstatus == ACTIVE AND adcampaign_name !@ Dynamic Feed";

// Available date ranges - canonical list used across the app
// Note: "today" removed since same-day data is not available
const DATE_RANGE_OPTIONS = [
  "yesterday",
  "week_to_date",
  "last_week_sun_sat",
  "last_7_days",
  "last_30_days",
  "this_month_to_yesterday",
  "last_month",
  "year_to_date"
];

// Header to database column mapping
// Note: Supermetrics may return different header names for the same field
const HEADER_MAP = {
  "Ad name": "ad_name",
  "Creative title": "creative_title",
  "Ad creative image URL": "ad_creative_image_url",
  "Creative image URL": "ad_creative_image_url",  // Alternative header name
  "Image URL": "ad_creative_image_url",  // Another possible header name
  // Preview URL mappings - all preview formats
  "Ad preview URL: Instagram story": "instagram_story_preview_url",
  "Ad preview URL: Instagram": "ad_preview_url_instagram",
  "Ad preview URL: Facebook story": "facebook_story_preview_url",
  "Ad preview URL: mobile feed": "mobile_feed_preview_url",
  "Ad preview URL: desktop feed": "desktop_feed_preview_url",
  "Ad preview URL: mobile interstitial": "mobile_interstitial_preview_url",
  "Ad preview URL: mobile banner": "mobile_banner_preview_url",
  "Ad preview URL: right column": "right_column_preview_url",
  "Ad ID": "ad_id",
  "Cost (USD)": "cost_usd",
  "Comparison value Cost (USD)": "comparison_cost_usd",
  "Impressions": "impressions",
  "Comparison value Impressions": "comparison_impressions", 
  "Unique link clicks": "unique_link_clicks",
  "Comparison value Unique link clicks": "comparison_unique_link_clicks",
  "CTR (link click-through rate)": "ctr_link_click_through_rate",
  "Comparison value CTR (link click-through rate)": "comparison_ctr_link_click_through_rate",
  "CPC (all)": "cpc_all",
  "Comparison value CPC (all)": "comparison_cpc_all",
  "CPM (cost per 1000 impressions)": "cpm_cost_per_1000_impressions",
  "Comparison value CPM (cost per 1000 impressions)": "comparison_cpm_cost_per_1000_impressions",
  "Frequency": "frequency",
  "Comparison value Frequency": "comparison_frequency",
  "Reach": "reach",
  "Comparison value Reach": "comparison_reach",
  "Quality ranking": "quality_ranking",
  "Comparison value Quality ranking": "comparison_quality_ranking",
  "Engagement rate ranking": "engagement_rate_ranking",
  "Comparison value Engagement rate ranking": "comparison_engagement_rate_ranking",
  "Return on ad spend (ROAS)": "roas",
  "Comparison value Return on ad spend (ROAS)": "comparison_roas"
};

// Data type definitions for proper casting
const INTEGER_FIELDS = new Set([
  "impressions", "comparison_impressions", "unique_link_clicks", 
  "comparison_unique_link_clicks", "reach", "comparison_reach",
  "quality_ranking", "comparison_quality_ranking", 
  "engagement_rate_ranking", "comparison_engagement_rate_ranking"
]);

const FLOAT_FIELDS = new Set([
  "cost_usd", "comparison_cost_usd", "ctr_link_click_through_rate",
  "comparison_ctr_link_click_through_rate", "cpc_all", "comparison_cpc_all",
  "cpm_cost_per_1000_impressions", "comparison_cpm_cost_per_1000_impressions",
  "frequency", "comparison_frequency", "roas", "comparison_roas"
]);

class SupermetricsService {
  constructor() {
    this.supabase = createClient(
      `https://${projectId}.supabase.co`,
      publicAnonKey
    );
  }

  /**
   * Build Supermetrics API URL with parameters
   */
  buildApiUrl(options = {}) {
    const {
      dateRangeType = 'last_week_sun_sat',
      filter = DEFAULT_FILTER,
      maxRows = DEFAULT_MAX_ROWS,
      apiKey,
      extra = {}
    } = options;

    if (!apiKey) {
      throw new Error('Supermetrics API key is required');
    }

    const payload = {
      ds_id: DS_ID,
      ds_accounts: DS_ACCOUNTS,
      ds_user: DS_USER,
      date_range_type: dateRangeType,
      compare_type: COMPARE_TYPE,
      compare_show: COMPARE_SHOW,
      fields: FIELDS,
      filter: filter,
      max_rows: maxRows,
      api_key: apiKey,
      ...extra
    };

    return `${API_BASE}?json=${encodeURIComponent(JSON.stringify(payload))}`;
  }

  /**
   * Fetch data from Supermetrics API
   */
  async fetchFromApi(url) {
    const response = await fetch(url);
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Supermetrics API error ${response.status}: ${errorText}`);
    }

    return response.json();
  }

  /**
   * Transform Supermetrics data to match database schema
   */
  transformData(apiResponse, dateRangeType) {
    const data = apiResponse?.data;
    if (!Array.isArray(data) || data.length < 2) {
      return [];
    }

    const headers = data[0];
    const rows = data.slice(1);

    return rows.map((row) => {
      const transformed = {};
      
      headers.forEach((header, index) => {
        const dbColumn = HEADER_MAP[header];
        if (!dbColumn) return; // Skip unmapped fields

        let value = row[index];
        
        // Handle null/empty values
        if (value === "" || value === undefined || value === null) {
          transformed[dbColumn] = null;
          return;
        }

        // Type casting
        if (INTEGER_FIELDS.has(dbColumn)) {
          transformed[dbColumn] = Number.isFinite(+value) ? parseInt(value, 10) : null;
        } else if (FLOAT_FIELDS.has(dbColumn)) {
          transformed[dbColumn] = Number.isFinite(+value) ? parseFloat(value) : null;
        } else {
          transformed[dbColumn] = String(value);
        }
      });

      // Add date range type for table partitioning
      transformed.date_range_type = dateRangeType;

      // Set safe defaults for required fields that might be missing
      transformed.ad_id = transformed.ad_id || null;
      transformed.ad_name = transformed.ad_name || null;
      transformed.creative_title = transformed.creative_title || null;
      transformed.ad_creative_image_url = transformed.ad_creative_image_url || null;
      transformed.ad_preview_url_instagram = transformed.ad_preview_url_instagram || null;

      return transformed;
    });
  }

  /**
   * Store data in Supabase table
   */
  async storeData(transformedData, tableName = 'adcanvas_facebook_ads') {
    if (!transformedData || transformedData.length === 0) {
      return { success: true, count: 0 };
    }

    const { data, error } = await this.supabase
      .from(tableName)
      .upsert(transformedData, {
        onConflict: 'ad_id,date_range_type',
        ignoreDuplicates: false
      });

    if (error) {
      throw new Error(`Database error: ${error.message}`);
    }

    return { success: true, count: transformedData.length, data };
  }

  /**
   * Trigger background image download for ads
   * This downloads images from CDN and stores them in Supabase Storage
   */
  async triggerImageDownload(adIds) {
    if (!adIds || adIds.length === 0) {
      return { success: true, triggered: 0 };
    }

    try {
      console.log(`[SupermetricsService] Triggering image download for ${adIds.length} ads`);

      const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/download-images`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': publicAnonKey,
          'Authorization': `Bearer ${publicAnonKey}`
        },
        body: JSON.stringify({ adIds })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[SupermetricsService] Image download trigger failed: ${errorText}`);
        return { success: false, error: errorText };
      }

      const result = await response.json();
      console.log(`[SupermetricsService] Image download result:`, result);
      return { success: true, ...result };

    } catch (error) {
      console.error(`[SupermetricsService] Error triggering image download:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Full ingestion workflow for a single date range
   */
  async ingestDateRange(dateRangeType, apiKey, options = {}) {
    const { skipImageDownload = false } = options;

    try {
      // 1. Build API URL
      const url = this.buildApiUrl({
        dateRangeType,
        apiKey,
        ...options
      });

      // 2. Fetch from Supermetrics
      const apiResponse = await this.fetchFromApi(url);

      // 3. Transform data
      const transformedData = this.transformData(apiResponse, dateRangeType);

      // 4. Store in Supabase
      const result = await this.storeData(transformedData);

      // 5. Trigger image download (background, non-blocking)
      // This downloads images from CDN while URLs are still fresh
      let imageDownloadResult = null;
      if (!skipImageDownload && result.count > 0) {
        const adIds = transformedData
          .map(record => record.ad_id)
          .filter(id => id != null);

        // Fire and forget - don't wait for image downloads
        this.triggerImageDownload(adIds)
          .then(res => {
            console.log(`[SupermetricsService] Background image download complete:`, res);
          })
          .catch(err => {
            console.error(`[SupermetricsService] Background image download error:`, err);
          });

        imageDownloadResult = { triggered: adIds.length };
      }

      return {
        success: true,
        dateRange: dateRangeType,
        count: result.count,
        data: transformedData,
        imageDownload: imageDownloadResult
      };

    } catch (error) {
      return {
        success: false,
        dateRange: dateRangeType,
        error: error.message
      };
    }
  }

  /**
   * Ingest multiple date ranges in parallel
   */
  async ingestMultipleRanges(dateRanges, apiKey, options = {}) {
    const results = await Promise.allSettled(
      dateRanges.map(range => this.ingestDateRange(range, apiKey, options))
    );

    const successful = [];
    const failed = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        if (result.value.success) {
          successful.push(result.value);
        } else {
          failed.push(result.value);
        }
      } else {
        failed.push({
          success: false,
          dateRange: dateRanges[index],
          error: result.reason?.message || 'Unknown error'
        });
      }
    });

    return {
      successful,
      failed,
      totalCount: successful.reduce((sum, result) => sum + result.count, 0)
    };
  }

  /**
   * Get available date range options
   */
  getDateRangeOptions() {
    return DATE_RANGE_OPTIONS.map(value => ({
      value,
      label: this.formatDateRangeLabel(value)
    }));
  }

  /**
   * Format date range value to human-readable label
   */
  formatDateRangeLabel(value) {
    const labels = {
      'yesterday': 'Yesterday',
      'week_to_date': 'Week to Date',
      'last_week_sun_sat': 'Last Week (Sun-Sat)',
      'last_7_days': 'Last 7 Days',
      'last_30_days': 'Last 30 Days',
      'this_month_to_yesterday': 'This Month to Yesterday',
      'last_month': 'Last Month',
      'year_to_date': 'Year to Date'
    };

    return labels[value] || value;
  }
}

// Export singleton instance
export const supermetricsService = new SupermetricsService();
export { DATE_RANGE_OPTIONS };